import { useEffect, useMemo, useRef } from 'react';
import { Payment } from '../types';
import { paymentsService, QuotaCheckResult } from '../services/supabase';
import { useInfiniteData } from './useInfiniteData';
import {
  useOptimisticCRUD,
  createOptimisticItem,
  OptimisticQuotaBlocked,
} from './useOptimisticCRUD';
import { useQuota } from '../context/QuotaContext';
import { PAYMENT_UPDATED_EVENT } from '../utils/paymentEvents';

interface UseInfinitePaymentsOptions {
  limit?: number;
  /** Optional: called when an update fails before rollback (e.g. show toast) */
  onUpdateFailed?: () => void;
}

interface PaymentCRUD {
  create: (data: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>) => void;
  update: (id: string, data: Payment, options?: { previousItem?: Payment }) => void;
  remove: (id: string) => void;
  bulkRemove: (ids: string[]) => void;
  bulkUpdate: (payments: Payment[]) => void;
}

interface UseInfinitePaymentsReturn {
  payments: Payment[];
  total?: number;
  loadingInitial: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Back-compat name for older call sites */
  silentRefresh: () => Promise<void>;
  reset: () => void;
  crud: PaymentCRUD;
  addPayment: (payment: Payment) => void;
}

export const useInfinitePayments = (options: UseInfinitePaymentsOptions = {}): UseInfinitePaymentsReturn => {
  const { limit = 30, onUpdateFailed } = options;
  const { showQuotaLimit } = useQuota();
  const listActionsRef = useRef({
    removeItem: (_id: string) => {},
    replaceItem: (_tempId: string, _item: Payment) => {},
    updateItem: (_item: Payment) => {},
  });

  const {
    items: payments,
    total,
    loadingInitial,
    loadingMore,
    refreshing,
    error,
    hasMore,
    loadMore,
    refresh,
    reset,
    addItem,
    updateItem,
    removeItem,
    replaceItem,
  } = useInfiniteData<Payment>({
    limit,
    cacheKey: 'payments',
    service: {
      getCursorPage: async (l, cursor) => {
        const result = await paymentsService.getCursorPage(l, cursor as any);
        return {
          items: result.payments,
          total: result.total,
          nextCursor: result.nextCursor,
          hasMore: result.hasMore,
        };
      },
    },
  });

  listActionsRef.current = { removeItem, replaceItem, updateItem };

  const optimisticCRUD = useOptimisticCRUD<
    Payment,
    Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>,
    QuotaCheckResult
  >({
    onOptimisticAdd: addItem,
    onOptimisticUpdate: updateItem,
    onOptimisticRemove: removeItem,
    onOptimisticReplace: replaceItem,
    service: {
      create: async (data) => {
        const result = await paymentsService.create(data) as any;
        if (result?.error === 'QUOTA_LIMIT') {
          return {
            __quotaBlocked: true,
            quotaInfo: result.quotaInfo as QuotaCheckResult,
          } satisfies OptimisticQuotaBlocked<QuotaCheckResult>;
        }
        return result && !result.error ? (result as Payment) : null;
      },
      update: async (id, data) => {
        return await paymentsService.update(id, data as Payment);
      },
      delete: async (id) => {
        return await paymentsService.delete(id);
      },
    },
    onQuotaBlocked: ({ tempId, data, quotaInfo }) => {
      showQuotaLimit(quotaInfo, {
        onDismiss: () => {
          listActionsRef.current.removeItem(tempId);
        },
        onSlotFreed: async () => {
          const created = await paymentsService.create(data) as any;
          if (created && !created.error && created.id) {
            listActionsRef.current.replaceItem(tempId, created as Payment);
            return;
          }
          listActionsRef.current.removeItem(tempId);
        },
      });
      return true;
    },
    onRollback: refresh,
    onUpdateFailed,
  });

  useEffect(() => {
    const onExternalUpdate = (event: Event) => {
      const payment = (event as CustomEvent<Payment>).detail;
      if (!payment?.id) return;
      listActionsRef.current.updateItem(payment);
    };

    window.addEventListener(PAYMENT_UPDATED_EVENT, onExternalUpdate);
    return () => window.removeEventListener(PAYMENT_UPDATED_EVENT, onExternalUpdate);
  }, []);

  const crud: PaymentCRUD = useMemo(
    () => ({
      create: (data) => {
        const optimisticPayment = createOptimisticItem<Payment>(data);
        optimisticCRUD.create(data, optimisticPayment);
      },
      update: (id, data, options) => optimisticCRUD.update(id, { ...data, updatedAt: new Date() }, options),
      remove: (id) => optimisticCRUD.remove(id),
      bulkRemove: (ids) => optimisticCRUD.bulkRemove(ids),
      bulkUpdate: (items) => {
        const updated = items.map((p) => ({ ...p, updatedAt: new Date() }));
        optimisticCRUD.bulkUpdate(updated);
      },
    }),
    [optimisticCRUD]
  );

  return {
    payments,
    total,
    loadingInitial,
    loadingMore,
    refreshing,
    error,
    hasMore,
    loadMore,
    refresh,
    silentRefresh: refresh,
    reset,
    crud,
    addPayment: addItem,
  };
};
